import * as Yup from "yup";
import {
  IssueRootCause,
  IssueTemplate,
  IssueType,
} from "@adsk/acc-issues-types";
import {
  getIssueTypeCategories,
  getIssueTypeEntitiesMap,
  getTypesFromCategory,
} from "@adsk/acc-issues-core/state/redux/ducks/issueTypes";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import {
  ExtendedRootCauseCategory,
  getIssueRootCausesEntitiesMap,
  getRootCausesFromRootCauseCategory,
  getSortedIssueRootCauseCategories,
} from "@adsk/acc-issues-core/state/redux/ducks/issueRootCauses";
import { FlattenedIssueTypeCategory } from "@adsk/acc-issues-core/utils/data/issueTypeUtils";
import { useTranslations } from "@adsk/acc-issues-translations";
import { getAllTemplates } from "@adsk/acc-issues-core/state/redux/ducks/issueTemplates";
import { useLocalizedDecimalSeparator } from "@adsk/acc-issues-core/utils/hooks/useLocalizedDecimalSeparator";

export const useTextValidation = ({
  maxLength = 50,
  isRequired = false,
}: {
  maxLength?: number;
  isRequired?: boolean;
} = {}) => {
  const { T } = useTranslations();
  let schema = Yup.string()
    .trim()
    .max(
      maxLength,
      T.translate("validation.maxLength", { maxLength }) as string
    );
  if (isRequired) {
    schema = schema.required(T.translate("validation.required") as string);
  }
  return schema;
};

export const useUnlimitedLengthTitleValidation = () => {
  const { T } = useTranslations();
  return Yup.string()
    .trim()
    .required(T.translate("validation.required") as string);
};

export const useTypeCategoryTitleValidation = ({
  titlesExcludedFromValidation,
}: {
  titlesExcludedFromValidation?: string[];
} = {}) => {
  const categories = useSelector(getIssueTypeCategories);
  const { T } = useTranslations();
  return useTextValidation({ isRequired: true }).test(
    "type-category-title-duplication",
    T.translate("validation.issueTypeCategory.alreadyExists") as string,
    categoryTitle => {
      const categoryTitleLowerCased =
        categoryTitle && categoryTitle.toLowerCase();
      return categories.every(
        ({ title }: FlattenedIssueTypeCategory) =>
          title.toLowerCase() !== categoryTitleLowerCased ||
          (titlesExcludedFromValidation &&
            titlesExcludedFromValidation.includes(title))
      );
    }
  );
};

export const useRootCauseCategoryTitleValidation = ({
  titlesExcludedFromValidation,
}: {
  titlesExcludedFromValidation?: string[];
} = {}) => {
  const categories = useSelector(getSortedIssueRootCauseCategories);
  const { T } = useTranslations();
  return useTextValidation({
    maxLength: 100,
    isRequired: true,
  }).test(
    "root-cause-category-title-duplication",
    T.translate("validation.rootCauseCategory.alreadyExists") as string,
    categoryTitle => {
      const categoryTitleLowerCased =
        categoryTitle && categoryTitle.toLowerCase();
      return categories.every(
        ({ title }: ExtendedRootCauseCategory) =>
          title.toLowerCase() !== categoryTitleLowerCased ||
          (titlesExcludedFromValidation &&
            titlesExcludedFromValidation.includes(title))
      );
    }
  );
};

export const useTypeTitleValidation = ({
  categoryId,
  titlesExcludedFromValidation,
}: {
  categoryId?: string;
  titlesExcludedFromValidation?: string[];
} = {}) => {
  const { T } = useTranslations();
  const entities = useSelector(getIssueTypeEntitiesMap);
  const category = categoryId && entities[categoryId];
  const typesInCategory =
    category && category.childrenIds
      ? getTypesFromCategory(category, entities)
      : [];
  return useTextValidation({ isRequired: true }).test(
    "type-title-duplication-within-category",
    T.translate("validation.issueType.alreadyExists") as string,
    titleToValidate => {
      const titleToValidateLowerCased =
        titleToValidate && titleToValidate.toLowerCase();
      return typesInCategory.every(
        ({ title }: IssueType) =>
          title.toLowerCase() !== titleToValidateLowerCased ||
          (titlesExcludedFromValidation &&
            titlesExcludedFromValidation.includes(title))
      );
    }
  );
};

export const useRootCauseTitleValidation = ({
  categoryId,
  titlesExcludedFromValidation,
}: {
  categoryId?: string;
  titlesExcludedFromValidation?: string[];
} = {}) => {
  const { T } = useTranslations();
  const entities = useSelector(getIssueRootCausesEntitiesMap);
  const category = categoryId && entities[categoryId];
  const rootCausesInCategory =
    category && category.childrenIds
      ? getRootCausesFromRootCauseCategory(category, entities)
      : [];
  return useTextValidation({
    maxLength: 100,
    isRequired: true,
  }).test(
    "root-cause-title-duplication-within-category",
    T.translate("validation.rootCause.alreadyExists") as string,
    titleToValidate => {
      const titleToValidateLowerCased =
        titleToValidate && titleToValidate.toLowerCase();
      return rootCausesInCategory.every(
        ({ title }: IssueRootCause) =>
          title.toLowerCase() !== titleToValidateLowerCased ||
          (titlesExcludedFromValidation &&
            titlesExcludedFromValidation.includes(title))
      );
    }
  );
};

export const useIssueTemplateTitleValidation = ({
  titlesExcludedFromValidation,
}: {
  titlesExcludedFromValidation?: string[];
} = {}) => {
  const templates = useSelector(getAllTemplates);
  const { T } = useTranslations();
  return useTextValidation({
    maxLength: 100,
    isRequired: true,
  }).test(
    "issue-template-title-duplication",
    T.translate("validation.issueTemplate.alreadyExists") as string,
    templateTitle => {
      const templateTitleLowerCased =
        templateTitle && templateTitle.toLowerCase();
      return templates.every(
        ({ issueAttributes: { title = "" } }: IssueTemplate) =>
          title.toLowerCase() !== templateTitleLowerCased ||
          (titlesExcludedFromValidation &&
            titlesExcludedFromValidation.includes(title))
      );
    }
  );
};

/**
 * For the atomic text fields, we just need to know if that field
 * passes validation, and we want to use our existing schemas.
 *
 * This function uses a yup schema to return us either an error message or nothing,
 * for an input value for a particular key in the schema.
 *
 * using this building block, we can create a validation function for use in
 * components like `AtomicTextField`
 */
export const getValidationMessageForAttribute = <T>({
  schema,
  attribute,
  value,
  index,
}: {
  schema: Yup.Schema<T>;
  attribute: string;
  value: any;
  index?: number;
}): string | void => {
  const fieldSchema = Yup.reach(
    schema,
    `${attribute}${index === undefined ? "" : `[${index}]`}`
  );
  try {
    fieldSchema.validateSync(value);
  } catch (error: any) {
    return error.message;
  }
};

const getLocalizedNumberRegex = (separator: string) =>
  new RegExp(`^[-]?[0-9]+(\\${separator}[0-9]+)?$`, "g");

export const useNumericFieldValidation = ({
  requirePositive = false,
}: {
  requirePositive?: boolean;
} = {}) => {
  const { T } = useTranslations();
  const { getLocalizedDecimalSeparator } = useLocalizedDecimalSeparator();

  const numberRegex = getLocalizedNumberRegex(getLocalizedDecimalSeparator());
  let validation = Yup.string()
    .nullable()
    .matches(numberRegex, {
      message: T.translate("validation.numeric") as string,
    })
    // there are some unusual test cases if the decimal place is put at the 16th digit,
    // and enough zeroes are added past that before adding new digits,
    // e.g. 123456789012345.001
    // To properly account for those we would need access to `originalValue`,
    // which is added in Yup 0.30 and above.
    // This is fine until we upgrade Yup though.
    .max(15, T.translate("validation.maxLength", { maxLength: 15 }) as string)
    .transform((value, originalValue) => {
      // see https://github.com/jquense/yup/issues/298
      // we want to make the empty string return as null
      if (typeof originalValue === "string" && originalValue === "") {
        return null;
      }

      return value;
    });

  if (requirePositive) {
    validation = validation.matches(/^[^-]*$/, {
      message: T.translate("validation.notNegative") as string,
    });
  }

  return validation;
};

export const isIssueTypePermitted = ({
  value,
  fixedTypeId,
  isVisibilityByTypeOn,
  allTypes,
  permittedTypeIds,
}: {
  value: string | undefined;
  fixedTypeId: string | undefined;
  isVisibilityByTypeOn: boolean;
  allTypes: boolean;
  permittedTypeIds: string[];
}): boolean => {
  if (!fixedTypeId || !isVisibilityByTypeOn || allTypes || !value) {
    return true;
  }
  return permittedTypeIds.includes(value);
};
